import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, SectionLabel } from "./Reveal";
import { ProjectStage } from "./ProjectStage";
import { CatNav, LogoWall } from "./GraphicParts";
import { PROJECTS, GRAPHIC_CATS } from "../data/content";
import { useLang } from "../lib/lang";

const Media = ({ p, onOpen, i }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 30 });
  const sy = useSpring(y, { stiffness: 300, damping: 30 });
  const [hover, setHover] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.6 });
  const touch = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - r.left);
    y.set(e.clientY - r.top);
  };
  return (
    <motion.button
      ref={ref}
      data-testid={`project-card-${p.id}`}
      onClick={onOpen}
      onMouseMove={move}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ clipPath: "inset(12% 12% 12% 12% round 28px)" }}
      whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 1.2, ease: [0.2, 0.7, 0.1, 1] }}
      className="stage-spot group relative block aspect-[4/3] w-full cursor-none overflow-hidden rounded-[28px] text-left ring-1 ring-white/10"
    >
      <ProjectStage p={p} play={hover || (touch && inView)} />
      <motion.span
        style={{ left: sx, top: sy }}
        animate={{ scale: hover ? 1 : 0 }}
        className="pointer-events-none absolute z-10 -ml-12 -mt-12 hidden h-24 w-24 place-items-center rounded-full bg-[var(--acid)] font-mono text-[11px] font-medium uppercase tracking-widest text-[var(--ink)] md:grid"
      >
        View
      </motion.span>
    </motion.button>
  );
};

export const Row = ({ p, i, total, onOpen }) => {
  const { tr } = useLang();
  const flip = i % 2 === 1;
  return (
    <article className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14">
      <div className={`min-w-0 lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <Media p={p} i={i} onOpen={onOpen} />
      </div>
      <FadeUp className={`min-w-0 lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <div className="flex items-baseline justify-between border-b border-white/10 pb-4">
          <span className="font-mono text-xs text-[var(--acid)]">{String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
          <span className="font-display text-2xl font-bold text-zinc-500">{p.kpi}</span>
        </div>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{tr(p.client)}</p>
        <h3 data-testid={`project-title-${p.id}`} className="mt-3 font-display text-[1.6rem] font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl">{tr(p.title)}</h3>
        <p className="mt-5 text-base leading-relaxed text-zinc-300">{tr(p.summary)}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {p.tags.map((t) => <span key={t} className="rounded-full border border-white/15 px-3 py-1 text-xs text-zinc-300">{t}</span>)}
        </div>
        <button data-testid={`project-open-btn-${p.id}`} onClick={onOpen} className="group mt-8 flex items-center gap-3 text-sm font-semibold">
          <span className="link-u">{tr({ es: "Ver caso completo", en: "View full case" })}</span>
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/20 transition-colors group-hover:bg-[var(--acid)] group-hover:text-[var(--ink)]"><ArrowUpRight size={16} /></span>
        </button>
      </FadeUp>
    </article>
  );
};

const Chapter = ({ c, onOpen }) => {
  const { tr } = useLang();
  const list = PROJECTS.filter((p) => p.cat === c.id);
  return (
    <div id={`cat-${c.id}`} data-testid={`graphic-chapter-${c.id}`} className="scroll-mt-28">
      <FadeUp className="mb-12 flex items-end justify-between gap-6 border-b border-white/10 pb-6">
        <h3 className="font-display text-3xl font-medium tracking-tight sm:text-5xl"><span className="mr-4 font-mono text-sm text-[var(--acid)] align-middle">{c.n}</span>{tr(c.title)}</h3>
        <span className="hidden font-mono text-xs uppercase tracking-widest text-zinc-400 sm:block">{c.id === "space" ? "Render · 3D" : `${list.length} ${tr({ es: list.length === 1 ? "proyecto" : "proyectos", en: list.length === 1 ? "project" : "projects" })}`}</span>
      </FadeUp>
      <div className="space-y-24 lg:space-y-32">
        {list.map((p, i) => <Row key={p.id} p={p} i={i} total={list.length} onOpen={() => onOpen(p)} />)}
        {c.id === "branding" && <LogoWall />}
      </div>
    </div>
  );
};

export const Works = ({ onOpen }) => {
  const { tr } = useLang();
  return (
    <section id="work" data-testid="graphic-design-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
      <div className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <SectionLabel index="01">{tr({ es: "Diseño gráfico", en: "Graphic design" })}</SectionLabel>
          <FadeUp as="h2" className="mt-6 font-display text-5xl font-semibold uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl">
            {tr({ es: "Gráfica", en: "Graphic" })}<br /><span className="font-serif-i normal-case text-[var(--acid)]">{tr({ es: "con intención.", en: "with intent." })}</span>
          </FadeUp>
        </div>
        <FadeUp className="max-w-sm text-zinc-400">
          {tr({ es: "Una selección ordenada por disciplina: de la identidad de marca al espacio físico. Toca cualquier proyecto para ver el caso.", en: "A selection organised by discipline: from brand identity to physical space. Tap any project to open the case." })}
        </FadeUp>
      </div>
      <CatNav />
      <div className="mt-20 space-y-32 lg:space-y-44">
        {GRAPHIC_CATS.map((c) => <Chapter key={c.id} c={c} onOpen={onOpen} />)}
      </div>
    </section>
  );
};
